import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { imageSrc } from "../../lib/api";
import { PAPER } from "../notes/EnvelopeMemories";

const LAYOUTS = {
  wide: { 1: [[15, 4, 70, 92]], 2: [[2, 4, 47, 92], [51, 4, 47, 92]], 3: [[2, 2, 47, 48], [51, 2, 47, 48], [26, 52, 48, 46]], 4: [[2, 2, 47, 48], [51, 2, 47, 48], [2, 52, 47, 46], [51, 52, 47, 46]] },
  narrow: { 1: [[3, 4, 94, 92]], 2: [[3, 2, 94, 48], [3, 52, 94, 46]] },
};
const GAP_MS = 1400;
const holdMs = (m) => 6500 + m.text.length * 30;

const LETTER = {
  in: { opacity: 0, y: 18 },
  show: (cut) => ({ opacity: 1, y: 0, transition: { duration: cut ? 0.3 : 1.8, ease: "easeInOut" } }),
  out: (cut) => ({ opacity: 0, y: -14, transition: { duration: cut ? 0.12 : 1.8, ease: "easeInOut" } }),
};

function Letter({ mem, lines, cut }) {
  return (
    <motion.div
      data-testid="bench-letter"
      data-memory-id={mem.id}
      variants={LETTER}
      custom={cut}
      initial="in"
      animate="show"
      exit="out"
      className="flex max-h-full max-w-full items-start gap-3 sm:gap-4"
    >
      <img
        data-testid="bench-letter-glimpse"
        src={imageSrc(mem.painting.image_url)}
        alt={mem.painting.title}
        className="mt-2 w-16 shrink-0 -rotate-3 border-[4px] border-white object-cover shadow-[0_12px_24px_-12px_rgba(91,58,133,0.6)] sm:w-20 sm:border-[6px]"
        style={{ aspectRatio: "4 / 5" }}
      />
      <div style={PAPER} className="min-w-0 max-w-[22rem] rounded-[5px] px-5 py-4 shadow-[0_22px_44px_-22px_rgba(91,58,133,0.55)]">
        <p
          data-testid="bench-letter-text"
          className="whitespace-pre-wrap break-words font-hand text-[21px] leading-[1.25] text-[#3B2656] sm:text-[24px]"
          style={{ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          {mem.text}
        </p>
        <p className="mt-2 truncate font-display text-sm italic text-[#7C6992]">beside “{mem.painting.title}”</p>
      </div>
    </motion.div>
  );
}

function Credits({ memories, onDone }) {
  const box = useRef(null);
  const inner = useRef(null);
  const [dist, setDist] = useState(null);
  useLayoutEffect(() => setDist({ from: box.current.offsetHeight, to: -inner.current.offsetHeight }), []);
  return (
    <div ref={box} data-testid="memory-credits" className="absolute inset-0 overflow-hidden bg-[radial-gradient(ellipse_20%_60%_at_50%_50%,rgba(255,250,253,0.38),rgba(255,250,253,0.12)_55%,transparent_75%)] [mask-image:linear-gradient(transparent,black_16%,black_84%,transparent)]">
      <motion.div
        ref={inner}
        initial={{ y: 4000 }}
        animate={dist ? { y: [dist.from, dist.to] } : undefined}
        transition={dist ? { duration: (dist.from - dist.to) / 26, ease: "linear" } : undefined}
        onAnimationComplete={() => dist && onDone()}
        className="mx-auto max-w-xl px-6 text-center"
      >
        <p className="font-display text-lg italic text-[#5B3A85] drop-shadow-[0_1px_8px_rgba(255,255,255,0.95)]">Memories left here</p>
        {memories.map((m) => (
          <div key={m.id} className="mt-10">
            <p className="whitespace-pre-wrap break-words font-hand text-2xl leading-snug text-[#3B2656] drop-shadow-[0_1px_8px_rgba(255,255,255,0.95)] sm:text-[28px]">{m.text}</p>
            <p className="mt-1 font-display text-sm italic text-[#5B3A85]">{m.painting.title}</p>
          </div>
        ))}
        <p className="mt-16 font-display text-lg italic text-[#5B3A85]">and yours, whenever you're ready</p>
      </motion.div>
    </div>
  );
}

function Glimpse({ painting }) {
  return (
    <motion.figure data-testid="bench-glimpse" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 0.92, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 1.8, delay: 1.2 }} className="flex max-h-full flex-col items-center gap-2">
      <img src={imageSrc(painting.thumb_url || painting.image_url)} alt={painting.title} className="max-h-[26vh] w-auto rotate-2 border-[6px] border-white object-cover shadow-[0_18px_36px_-18px_rgba(91,58,133,0.7)]" />
      <figcaption className="font-display text-sm italic text-[#F3E8FF]/85">{painting.title}</figcaption>
    </motion.figure>
  );
}

export function MemoryReel({ memories, flow, narrow, blinkRef, paintings = [] }) {
  const flowRef = useRef(flow);
  flowRef.current = flow;
  const st = useRef({ next: 0, shown: 0, loop: 0, slots: [], free: [], phase: "letters", swaps: 0 });
  const [view, setView] = useState({ phase: "letters", slots: [], cut: false });

  const stagger = (s) => {
    const now = Date.now();
    s.free = [0, 1, 2, 3].map((i) => now + 1400 + i * 1500);
  };
  useEffect(() => {
    const s = st.current;
    stagger(s);
    const tick = () => {
      if (s.phase !== "letters") return;
      const now = Date.now();
      let changed = false;
      for (let i = 0; i < 4; i++) {
        const cur = s.slots[i];
        if (cur && now > cur.until) {
          s.slots[i] = null;
          s.free[i] = now + GAP_MS;
          changed = true;
        } else if (!cur && i < flowRef.current && s.shown < memories.length && now > s.free[i]) {
          const m = memories[s.next++ % memories.length];
          s.shown++;
          s.slots[i] = { m, key: `${m.id}-${s.loop}`, until: now + holdMs(m) };
          changed = true;
        }
      }
      if (s.shown >= memories.length && s.slots.every((x) => !x)) {
        s.phase = "credits";
        changed = true;
      }
      if (changed) setView({ phase: s.phase, slots: [...s.slots], cut: false });
    };
    const t = setInterval(tick, 400);
    blinkRef.current = () => {
      if (s.phase !== "letters") return;
      const now = Date.now();
      for (let i = 0; i < flowRef.current; i++) {
        if (!s.slots[i] && s.shown >= memories.length) continue;
        if (s.shown >= memories.length) {
          s.slots[i] = null;
          s.free[i] = now + GAP_MS;
          continue;
        }
        const m = memories[s.next++ % memories.length];
        s.shown++;
        s.slots[i] = { m, key: `${m.id}-${s.loop}-b${s.swaps}`, until: now + holdMs(m) };
      }
      s.swaps++;
      setView({ phase: s.phase, slots: [...s.slots], cut: true });
    };
    window.__afaeBench = () => ({ phase: s.phase, shown: s.shown, total: memories.length, loop: s.loop, flow: flowRef.current, swaps: s.swaps, showing: s.slots.filter(Boolean).map((x) => x.m.id) });
    return () => {
      clearInterval(t);
      blinkRef.current = null;
      delete window.__afaeBench;
    };
  }, [memories, blinkRef]);

  const restart = () => {
    const s = st.current;
    Object.assign(s, { phase: "letters", shown: 0, next: 0, slots: [], loop: s.loop + 1 });
    stagger(s);
    s.free = s.free.map((f) => f - 2000);
    setView({ phase: "letters", slots: [], cut: false });
  };

  if (view.phase === "credits") return <Credits key={st.current.loop} memories={memories} onDone={restart} />;
  const set = LAYOUTS[narrow ? "narrow" : "wide"];
  const max = narrow ? 2 : 4;
  const used = view.slots.reduce((m, x, i) => (x ? i + 1 : m), 0);
  const n = Math.min(max, Math.max(flow, used));
  const regions = set[n];
  return (
    <div data-testid="memory-reel" className="absolute inset-0">
      {regions.map((r, i) => {
        const slot = view.slots[i];
        const lines = r[3] < 60 ? 5 : 9;
        // with fewer letters than slots, the spare slot shows a painting glimpse instead of standing empty
        const spare = !slot && i >= memories.length && paintings[i];
        return (
          <div
            key={i}
            data-testid={`bench-slot-${i}`}
            className="absolute flex items-center justify-center overflow-hidden p-2 transition-[left,top,width,height] duration-1000"
            style={{ left: `${r[0]}%`, top: `${r[1]}%`, width: `${r[2]}%`, height: `${r[3]}%` }}
          >
            <AnimatePresence mode="wait" custom={view.cut}>{slot ? <Letter key={slot.key} mem={slot.m} lines={lines} cut={view.cut} /> : spare ? <Glimpse key={`g-${spare.id}`} painting={spare} /> : null}</AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
